import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLicensing } from '../../hooks/useLicensing';
import { EntitlementState, normalizeEntitlement } from '../../services/entitlement';
import { LicensingStatus } from '../../services/licensingService';
import { useSupabase } from '../../context/SupabaseContext';

interface LicensingContextValue {
  entitlementState: EntitlementState;
  status: LicensingStatus | null;
  isLoading: boolean;
  refreshEntitlement: () => Promise<LicensingStatus | null>;
}

const LicensingContext = createContext<LicensingContextValue | null>(null);

export function LicensingProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { salonId, isLoading: isSalonContextLoading } = useSupabase();
  const { status, isLoading, error, refreshLicense } = useLicensing({
    salonId,
    isSalonContextLoading,
  });
  const entitlementState = useMemo(
    () => normalizeEntitlement(status, Boolean(error)),
    [error, status]
  );

  useEffect(() => {
    if (location.pathname !== '/billing/success') return;

    const timer = window.setTimeout(() => {
      void refreshLicense();
    }, 2500);

    return () => window.clearTimeout(timer);
  }, [location.pathname, refreshLicense]);

  const value = useMemo<LicensingContextValue>(() => ({
    entitlementState,
    status,
    isLoading,
    refreshEntitlement: refreshLicense,
  }), [entitlementState, isLoading, refreshLicense, status]);

  return <LicensingContext.Provider value={value}>{children}</LicensingContext.Provider>;
}

export function useEntitlement(): LicensingContextValue {
  const value = useContext(LicensingContext);
  if (!value) {
    throw new Error('useEntitlement must be used within LicensingProvider');
  }
  return value;
}
